import React from "react";
import { Row, Col } from "reactstrap";

/**
 * Grid wrapper for a row of ModuleShortcutCard tiles. Provides an
 * optional group heading (icon + title + description) and consistent
 * column breakpoints matching the existing Mafia MenuPage layout.
 *
 * Pure layout — does not own any state, does not fetch anything.
 *
 * Props
 *  - title:        optional group heading text
 *  - icon:         ri-* icon for group heading
 *  - description:  small helper line next to the heading
 *  - columns:      breakpoint map { xxl, xl, lg, md, sm } — defaults to
 *                  { xxl: 3, xl: 4, md: 6 } to match Mafia's tile grid
 *  - gutter:       bootstrap g-* gutter class number (default 3)
 *  - className:    extra wrapper className
 *  - children:     ModuleShortcutCard nodes (already wrapped in <Col>?)
 *                  If a child is NOT a <Col>, it's auto-wrapped using
 *                  the column breakpoints above.
 */
const ModuleLandingGrid = ({
  title,
  icon,
  description,
  columns = { xxl: 3, xl: 4, md: 6 },
  gutter = 3,
  className = "",
  children,
}) => {
  const items = React.Children.toArray(children).filter(Boolean);

  return (
    <div className={`mb-4 ${className}`}>
      {(title || description) ? (
        <div className="d-flex align-items-center gap-2 mb-2 px-1 flex-wrap">
          {icon ? (
            <i
              className={icon}
              style={{ fontSize: 16, color: "#405189" }}
              aria-hidden="true"
            />
          ) : null}
          {title ? (
            <h6 className="fw-semibold mb-0" style={{ color: "#212529" }}>
              {title}
            </h6>
          ) : null}
          {description ? (
            <span
              className="text-muted small"
              style={{ fontSize: 12, marginLeft: 8 }}
            >
              {description}
            </span>
          ) : null}
        </div>
      ) : null}

      <Row className={`g-${gutter}`}>
        {items.map((child, idx) => {
          // If consumer already wrapped in a <Col>, respect that.
          if (
            React.isValidElement(child) &&
            child.type &&
            (child.type === Col || child.type.displayName === "Col")
          ) {
            return React.cloneElement(child, { key: child.key ?? idx });
          }
          return (
            <Col {...columns} key={idx}>
              {child}
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default ModuleLandingGrid;
